import { z } from 'zod';

import { PREVIEW_MAX_LENGTH } from '../_shared/preview';
import { CardIdSchema } from '../card/card_id';
import { CardCompanySnapshotSchema, CardCustomerSnapshotSchema } from '../card/card';
import { SquadIdSchema } from '../project/project';

/**
 * One ticket as the operator sees it on the "Visão geral de
 * atendimento" landing — flattened so each row carries everything the
 * overview buckets need to render without joining tasks, agents, or
 * SLAs at render time.
 *
 * `squadId`/`squadName` are `null` for a card claimed straight from the
 * triage inbox (operator assigned, no squad yet) — only `ongoing` rows
 * can be squad-less; the awaiting queue is always squad'd.
 */
export const OverviewRowSchema = z.object({
  cardId: CardIdSchema,
  squadId: SquadIdSchema.nullable(),
  squadName: z.string().min(1).max(80).nullable(),
  customer: CardCustomerSnapshotSchema,
  company: CardCompanySnapshotSchema,
  preview: z.string().max(PREVIEW_MAX_LENGTH),
});

export type OverviewRow = z.infer<typeof OverviewRowSchema>;

/**
 * Triage snapshot — cards without a squad that need human routing.
 * Powers the pill chip on the overview header (`tri-pill` in the
 * mockup). Pure counter; the triage view loads its own card list.
 */
export const OverviewTriageSchema = z.object({
  total: z.number().int().min(0),
});

export type OverviewTriage = z.infer<typeof OverviewTriageSchema>;

/**
 * Buckets of the overview view:
 *   - `ongoing` — currently being handled by the requesting agent.
 *   - `vip`     — VIP customers waiting in the human queue.
 *   - `queue`   — remaining waiting tickets.
 *
 * Counts at the bottom power the header strip (IA / humano / fila).
 * `triage` lights up the routing-pending chip next to the sub-line.
 */
export const OverviewSchema = z.object({
  ongoing: z.array(OverviewRowSchema),
  vip: z.array(OverviewRowSchema),
  queue: z.array(OverviewRowSchema),
  aiCount: z.number().int().min(0),
  humanCount: z.number().int().min(0),
  waitingTotal: z.number().int().min(0),
  triage: OverviewTriageSchema,
});

export type Overview = z.infer<typeof OverviewSchema>;
